import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchRequisitos, removeRequisito } from '../../reducers/requisito_slice';
import RequisitoForm from './RequisitoForm';
import Table from 'react-bootstrap/Table';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import ActionButton from '../UI/Buttons/ActionButton';
import CustomPrintModal from '../UI/CustomPrintModal';

const RequisitoList = () => {
  const dispatch = useDispatch();
  const requisitos = useSelector(state => state.requisito.requisitos || []);
  const [showModal, setShowModal] = useState(false);
  const [currentRequisito, setCurrentRequisito] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [printContent, setPrintContent] = useState({ head: [], body: [] });
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchRequisitos());
  }, [dispatch]);

  const handleDelete = id => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: "¡No podrás revertir esto!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#464646',
      confirmButtonText: 'Sí, eliminarlo!',
       cancelButtonText: 'Cancelar',
      reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(removeRequisito(id)).unwrap().then(() => {
          Swal.fire('¡Eliminado!', 'El requisito ha sido eliminado.', 'success');
        }).catch((error) => {
          Swal.fire('Error', error.error || 'Ocurrió un error al eliminar el requisito', 'error');
        });
      }
    });
  };

  const handleSort = (key) => {
    if (sortConfig.direction === 'desc') {
      setSortConfig({ key: null, direction: 'asc' });
      return;
    }

    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = null;
    }

    setSortConfig({ key, direction });
  };

  const handleEdit = requisito => {
    setCurrentRequisito(requisito);
    setShowModal(true);
  };

  const handleCreate = () => {
    setCurrentRequisito(null);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setCurrentRequisito(null);
  };

  const handleBack = () => {
    navigate('/admin'); // Ruta a la que deseas regresar, en este caso, la página principal
  };

  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(sortedRequisitos);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Requisitos");
    XLSX.writeFile(workbook, "requisitos.xlsx");
  };

  const sortedRequisitos = useMemo(() => {
    let sortedData = [...requisitos];
    if (sortConfig.key) {
      sortedData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortedData;
  }, [requisitos, sortConfig]);

  const handleShowPreview = () => {
    setPrintContent(getPreviewContent());
    setShowPreviewModal(true);
  };

  const handleClosePreview = () => {
    setShowPreviewModal(false);
  };

  const getPreviewContent = () => {
    const content = {
      head: [['Nombre', 'Detalle', 'Estado']],
      body: sortedRequisitos.map(requisito => [
        requisito.nombre,
        requisito.detalle,
        requisito.estado
      ])
    };
    return content;
  };

  return (
    <div className="list-container list0">
      <h1 className='table-title'>Lista de Requisitos</h1>
      <div className="list-content">
        <div className='list-controls'>
          <div className="col-md-6">
          </div>
          <div className="col-md-6 text-right">
            <ActionButton onClick={handleCreate} outline icon='plus' floatEnd>
              Nuevo Requisito
            </ActionButton>
          </div>
        </div>
        <div className="list-container-header">
          <div className="left-buttons">
            <button title="Volver" onClick={handleBack} className="icon-button btn-transparent"><i className="bi bi-arrow-left"></i></button>
          </div>
          <div className="right-buttons">
            <button title="Imprimir" onClick={handleShowPreview} className="icon-button btn-transparent"><i className="bi bi-printer"></i></button>
            <button title="Exportar a excel" onDoubleClick={handleExport} className="icon-button btn-transparent"><i className="bi bi-file-spreadsheet"></i></button>
          </div>
        </div>
        {requisitos.length === 0 ? (
          <p>No hay requisitos disponibles.</p>
        ) : (
          <div className='table-container'>
            <Table bordered hover size='sm'>
              <thead className='text-center align-middle'>
                <tr>
                  <th className='col-index'>N°</th>
                  <th onClick={() => handleSort('nombre')}>
                    Nombre
                    {sortConfig.key === 'nombre' && (
                      sortConfig.direction === 'asc' ? <i className="bi bi-arrow-up"></i> : <i className="bi bi-arrow-down"></i>
                    )}
                  </th>
                  <th onClick={() => handleSort('detalle')}>
                    Detalle
                    {sortConfig.key === 'detalle' && (
                      sortConfig.direction === 'asc' ? <i className="bi bi-arrow-up"></i> : <i className="bi bi-arrow-down"></i>
                    )}
                  </th>
                  <th onClick={() => handleSort('estado')}>
                    Estado
                    {sortConfig.key === 'estado' && (
                      sortConfig.direction === 'asc' ? <i className="bi bi-arrow-up"></i> : <i className="bi bi-arrow-down"></i>
                    )}
                  </th>
                  <th className='col-simple-actions'>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sortedRequisitos.map((requisito, index) => (
                  <tr key={requisito.id}>
                    <td>{index + 1}</td>
                    <td>{requisito.nombre}</td>
                    <td>{requisito.detalle}</td>
                    <td>{requisito.estado}</td>
                    <td>
                      <button className="icon-button btn-secondary-outline" title="Editar" onClick={() => handleEdit(requisito)}>
                        <i className="bi bi-pencil"></i>
                      </button>
                      <button className="icon-button btn-secondary-outline" title="Eliminar" onClick={() => handleDelete(requisito.id)}>
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        <RequisitoForm currentRequisito={currentRequisito} showModal={showModal} handleClose={handleClose} />
        <CustomPrintModal
          show={showPreviewModal}
          handleClose={handleClosePreview}
          title1="LISTA DE REQUISITOS"
          title2=""
          content={printContent}
          copia={false}
        />
      </div>
    </div>
  );
};

export default RequisitoList;
